import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { useDispatch } from 'react-redux'; 
import * as Yup from 'yup';
import user from './../../../../../images/user.jpg';
import hidePasswordIcon from "../../../../../assets/icons/hide-password.svg";
import showPasswordIcon from "../../../../../assets/icons/show-password.svg";
import {
  useGetStudentProfileQuery,
  useUpdateStudentProfileMutation,
  useUpdatePasswordMutation,
} from '../../../../../store/services/student/ProfileService';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../components/PluginsMenu/Toastr/Toastr';
import LanguageSelect from './LanguageSelect';
import CountryCodeSelect from '../../../../components/common/CountryCodeSelect';
import Select from 'react-select'; // Import react-select

const fieldMapping = {
  Name: 'Name',
  Email: 'Email',
  Gender: 'Gender',
  Age: 'Age',
  Location: 'Location',
  'Native Language': 'NativeLanguageId',
  'Learning Language': 'Language',
  'Interested In': 'InterestedIn',
  'Purpose of Language Practice': 'PurposeOfLanguagePractice',
  Timezone: 'Timezone',
  'Phone Number': 'PhoneNumber',
};

// Yup schema for profile form
const ProfileSchema = Yup.object().shape({
  Name: Yup.string().trim().required('Name is required').matches(/^[A-Za-z\s]+$/, 'Name should only contain alphabets').max(50, 'Max 50 characters'),
  Email: Yup.string().required('Email is required').email('Invalid email format'),
  Gender: Yup.string(),
  Age: Yup.number()
    .integer('Age must be a whole number')
    .min(18, 'Minimum age is 18')
    .max(100, 'Maximum age is 100'),
  Location: Yup.string().trim().required('Location is required'),
  NativeLanguageId: Yup.string().required('Native language is required'),
  Language: Yup.string().required('Learning language is required'),
  InterestedIn: Yup.string().required('Interested In is required'),
  PurposeOfLanguagePractice: Yup.string().required('Purpose of language practice is required'),
  Timezone: Yup.string().required('Timezone is required'),
  PhoneNumber: Yup.string()
    .test('phone-length', 'Phone number must be between 7 and 15 digits', function(value) {
      // Phone number is optional, so if empty or null, it's valid
      if (!value || value.trim() === '') return true;

      const digitsOnly = value.replace(/\D/g, '');
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    }),
});

// Yup schema for password form
const PasswordSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Old password is required'),
  newPassword: Yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Must contain one uppercase letter')
    .matches(/[0-9]/, 'Must contain one number'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords must match')
    .required('Confirm password is required'),
});

// Custom styles for react-select
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
    boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#86b7fe' : '#dee2e6'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e7f1ff' : 'white',
    color: state.isSelected ? 'white' : 'black',
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px'
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

export const StudentPersonalDetails = () => {
  const { data, isLoading, refetch } = useGetStudentProfileQuery();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateStudentProfileMutation();
  const [updatePassword, { isLoading: isUpdatingPassword }] =
    useUpdatePasswordMutation();

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [learningLanguages, setLearningLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);
  const [hasExistingEmail, setHasExistingEmail] = useState(false);
  const [isSocialUser, setIsSocialUser] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (data?.data?.profilePicture) {
      const fullImageUrl = data?.data?.isSocialSignup ? data.data.profilePicture : `${process.env.REACT_APP_IMAGE_PATH}${data.data.profilePicture}`;
      setPreviewUrl(fullImageUrl);
    }
    
    // Check if email already exists in the profile
    if (data?.data?.email) {
      setHasExistingEmail(true);
    }

    // Get social signup status
    setIsSocialUser(data?.data?.isSocialSignup || false);
  }, [data]);

  // Fetch languages from API for nativeLanguage
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch('https://api.nkenne.com/api/Language/GetAllLanguage');
        const data = await response.json();
        setLanguages(data);
      } catch (error) {
        console.error('Failed to fetch languages:', error);
      }
    };
    fetchLanguages();
  }, []);

  // Fetch learning languages from API for learningLanguage
  useEffect(() => {
    const fetchLearningLanguages = async () => {
      try {
        const response = await fetch('https://api.nkenne.com/api/Language/GetAllLearningLanguage');
        const data = await response.json();
        setLearningLanguages(data);
      } catch (error) {
        console.error('Failed to fetch learning languages:', error);
      }
    };
    fetchLearningLanguages();
  }, []);

  // Fetch countries for location dropdown
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://restcountries.com/v3.1/all?fields=name,flags,cca2');
        const data = await response.json();
        
        // Sort countries alphabetically
        const sortedCountries = data.sort((a, b) => 
          a.name.common.localeCompare(b.name.common)
        );
        
        setCountries(sortedCountries);
      } catch (error) {
        console.error('Failed to fetch countries:', error);
        // Fallback to common countries if API fails
        setCountries([
          { name: { common: 'United States' }, flags: { png: '' }, cca2: 'US' },
          { name: { common: 'United Kingdom' }, flags: { png: '' }, cca2: 'GB' },
          { name: { common: 'Canada' }, flags: { png: '' }, cca2: 'CA' },
          { name: { common: 'Australia' }, flags: { png: '' }, cca2: 'AU' },
          { name: { common: 'Germany' }, flags: { png: '' }, cca2: 'DE' },
          { name: { common: 'France' }, flags: { png: '' }, cca2: 'FR' },
          { name: { common: 'Japan' }, flags: { png: '' }, cca2: 'JP' },
          { name: { common: 'South Korea' }, flags: { png: '' }, cca2: 'KR' },
          { name: { common: 'China' }, flags: { png: '' }, cca2: 'CN' },
          { name: { common: 'India' }, flags: { png: '' }, cca2: 'IN' },
        ]);
      }
    };
    fetchCountries();
  }, []);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countries;
    
    return countries.filter(country =>
      country.name.common.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (country.cca2 && country.cca2.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [countries, searchQuery]);

  const timezoneOptions = useMemo(() => [
    { value: 'UTC−10  Pacific/Honolulu', label: 'UTC−10  Pacific/Honolulu' },
    { value: 'UTC−09  America/Anchorage', label: 'UTC−09  America/Anchorage' },
    { value: 'UTC−08  America/Los_Angeles', label: 'UTC−08  America/Los_Angeles' },
    { value: 'UTC−07  America/Denver', label: 'UTC−07  America/Denver' },
    { value: 'UTC−06  America/Chicago', label: 'UTC−06  America/Chicago' },
    { value: 'UTC−05  America/New_York', label: 'UTC−05  America/New_York' },
    { value: 'UTC−04  America/Barbados', label: 'UTC−04  America/Barbados' },
    { value: 'UTC−03  America/Sao_Paulo', label: 'UTC−03  America/Sao_Paulo' },
    { value: 'UTC−02  Atlantic/South_Georgia', label: 'UTC−02  Atlantic/South_Georgia' },
    { value: 'UTC−01  Atlantic/Azores', label: 'UTC−01  Atlantic/Azores' },
    { value: 'UTC±00  Europe/London', label: 'UTC±00  Europe/London' },
    { value: 'UTC+01  Africa/Lagos', label: 'UTC+01  Africa/Lagos' },
    { value: 'UTC+02  Africa/Cairo', label: 'UTC+02  Africa/Cairo' },
    { value: 'UTC+03  Africa/Nairobi', label: 'UTC+03  Africa/Nairobi' },
    { value: 'UTC+03:30  Asia/Tehran', label: 'UTC+03:30  Asia/Tehran' },
    { value: 'UTC+04  Asia/Dubai', label: 'UTC+04  Asia/Dubai' },
    { value: 'UTC+04:30  Asia/Kabul', label: 'UTC+04:30  Asia/Kabul' },
    { value: 'UTC+05  Asia/Karachi', label: 'UTC+05  Asia/Karachi' },
    { value: 'UTC+05:30  Asia/Kolkata', label: 'UTC+05:30  Asia/Kolkata' },
    { value: 'UTC+05:45  Asia/Kathmandu', label: 'UTC+05:45  Asia/Kathmandu' },
    { value: 'UTC+06  Asia/Dhaka', label: 'UTC+06  Asia/Dhaka' },
    { value: 'UTC+06:30  Asia/Yangon', label: 'UTC+06:30  Asia/Yangon' },
    { value: 'UTC+07  Asia/Bangkok', label: 'UTC+07  Asia/Bangkok' },
    { value: 'UTC+08  Asia/Shanghai', label: 'UTC+08  Asia/Shanghai' },
    { value: 'UTC+09  Asia/Tokyo', label: 'UTC+09  Asia/Tokyo' },
    { value: 'UTC+09:30  Australia/Darwin', label: 'UTC+09:30  Australia/Darwin' },
    { value: 'UTC+10  Australia/Sydney', label: 'UTC+10  Australia/Sydney' },
    { value: 'UTC+11  Pacific/Guadalcanal', label: 'UTC+11  Pacific/Guadalcanal' },
    { value: 'UTC+12  Pacific/Auckland', label: 'UTC+12  Pacific/Auckland' },
    { value: 'UTC+13  Pacific/Tongatapu', label: 'UTC+13  Pacific/Tongatapu' },
    { value: 'UTC+14  Pacific/Kiritimati', label: 'UTC+14  Pacific/Kiritimati' }
  ], []);

  const genderOptions = ['Male', 'Female', 'Other'];

  // Format countries for react-select - MOVED BEFORE THE EARLY RETURN
  const countryOptions = useMemo(() => 
    filteredCountries.map(country => ({
      value: country.name.common,
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {country.flags.png && (
            <img 
              src={country.flags.png}
              alt={country.name.common}
              style={{ width: '20px', height: '15px' }}
            />
          )}
          <span>{country.name.common}</span>
        </div>
      ),
      raw: country
    })),
    [filteredCountries]
  );

  // Map API response to form initial values
  const initialProfileValues = {
    Name: data?.data?.name || '',
    Email: data?.data?.email || '',
    Gender: data?.data?.gender || '',
    Age: data?.data?.age || '',
    Location: data?.data?.location || '',
    NativeLanguageId: data?.data?.nativeLanguageId?.toString() || '',
    Language: data?.data?.languageId?.toString() || '',
    InterestedIn: data?.data?.interested || '',
    PurposeOfLanguagePractice: data?.data?.purposeLanguage || '',
    Timezone: data?.data?.timezone || '',
    PhoneNumber: data?.data?.phoneNumber || '',
  };

  // Find selected country option - MOVED BEFORE THE EARLY RETURN
  const selectedCountryOption = initialProfileValues.Location 
    ? countryOptions.find(option => option.value === initialProfileValues.Location)
    : null;

  // EARLY RETURN MUST COME AFTER ALL HOOKS
  if (isLoading) return <p>Loading profile...</p>;

  const initialPasswordValues = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const handleImageChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setPreviewUrl(URL.createObjectURL(selectedFile));
    }
  };

  const updateLocalStorageProfile = (updatedData) => {
    try {
      const authPersist = localStorage.getItem('persist:auth');
      if (authPersist) {
        const authData = JSON.parse(authPersist);
        const currentUser = JSON.parse(authData.currentUser || '{}');
        
        // Merge with updated data
        const updatedUser = {
          ...currentUser,
          ...updatedData
        };
        
        // Update localStorage
        authData.currentUser = JSON.stringify(updatedUser);
        localStorage.setItem('persist:auth', JSON.stringify(authData));
        
        return updatedUser;
      }
    } catch (error) {
      console.error('Error updating localStorage:', error);
    }
    return null;
  };

  const handleSubmitProfile = async (values, { setSubmitting }) => {
    console.log("Submitting values:", values);
    
    try {
      const payload = new FormData();
      
      // Convert Age to integer as required by API
      const processedValues = {
        ...values,
        Age: values.Age ? parseInt(values.Age) : null,
        NativeLanguageId: values.NativeLanguageId ? parseInt(values.NativeLanguageId) : null,
        Language: values.Language ? parseInt(values.Language) : null,
      };

      // Append all values to FormData with proper field names
      Object.entries(processedValues).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          payload.append(key, value.toString());
        }
      });
      
      if (file) {
        payload.append('ProfilePicture', file);
      }

      console.log("FormData entries:");
      for (let pair of payload.entries()) {
        console.log(pair[0] + ': ' + pair[1]);
      }

      const result = await updateProfile(payload).unwrap();
      console.log("Update successful:", result);
      
      showSuccessToast('Profile updated successfully');
      await refetch();
      
      // Update hasExistingEmail state after successful update
      if (values.Email) {
        setHasExistingEmail(true);
      }

      if (result.data && result.data.data) {
        const updatedUserData = result.data.data;
        
        // Prepare data for update
        const profileUpdateData = {
          imageUrl: updatedUserData.imageUrl,
          fullName: updatedUserData.fullName,
          email: updatedUserData.email,
          interested: updatedUserData.interested,
          purposeLanguage: updatedUserData.purposeLanguage
        };
        
        // Clean undefined values
        Object.keys(profileUpdateData).forEach(key => 
          profileUpdateData[key] === undefined && delete profileUpdateData[key]
        );
        
        // Update localStorage
        const updatedUser = updateLocalStorageProfile(profileUpdateData);
        
        if (updatedUser) {
          // Dispatch to Redux
          dispatch({
            type: "UPDATE_USER_PROFILE",
            payload: updatedUser
          });
        }
      }
    } catch (error) {
      console.error('Update failed:', error);
      showErrorToast(error.data?.message || 'Failed to update profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitPassword = async (values, { setSubmitting, resetForm }) => {
    try {
      await updatePassword(values).unwrap();
      showSuccessToast('Password updated successfully');
      resetForm();
    } catch (error) {
      console.error('Password update failed:', error);
      showErrorToast(error.data?.errors?.[0] || 'Failed to update password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="row">
      {/* Profile Image */}
      <div className="card">
        <div className="p-5">
          <div className="author-profile">
            <div className="author-media">
              <img
                src={previewUrl || user}
                alt="user-profile"
                className="rounded-circle border"
                height="130"
                width="130"
                style={{ objectFit: 'cover' }}
              />
              <div
                className="upload-link"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  className="update-flie"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
                <i className="fa fa-camera" />
              </div>
            </div>
            <div className="author-info">
              <h6 className="title">{initialProfileValues.Name}</h6>
              <span>Student</span>
            </div>
          </div>
        </div>
      </div>

      {/* Update Profile */}
      <div className="card profile-card card-bx h-auto">
        <div className="card-header">
          <h6 className="title">Account Details</h6>
        </div>
        <Formik
          initialValues={initialProfileValues}
          validationSchema={ProfileSchema}
          onSubmit={handleSubmitProfile}
          enableReinitialize
        >
          {({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => (
            <Form className="profile-form" onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="row">
                  {Object.keys(fieldMapping).map((label, ind) => {
                    const fieldName = fieldMapping[label];
                    return (
                      <div className="col-sm-6 m-b30" key={ind}>
                        <label className="form-label">
                          {label}
                          {(label === 'Name' || label === 'Email' || label === 'Location' || label === 'Timezone' || 
                            label === 'Learning Language' || label === 'Interested In' || 
                            label === 'Purpose of Language Practice') && (
                            <span className="text-danger"> *</span>
                          )}
                        </label>
                        {label === 'Phone Number' ? (
                          <div className="col-12">
                            <CountryCodeSelect
                              value={values.PhoneNumber} // Full phone number with country code
                              onChange={(phone) => setFieldValue('PhoneNumber', phone)}
                              placeholder="Enter phone number"
                            />
                            <small className="text-muted d-block mt-1">
                              Include country code (e.g., +234123456789)
                            </small>
                          </div>
                        ) : label === 'Email' ? (
                          <Field
                            type="email"
                            name={fieldName}
                            className="form-control"
                            disabled={hasExistingEmail}
                            placeholder="Enter your email"
                            title={hasExistingEmail ? "Email cannot be changed once set" : ""}
                          />
                        ) : label === 'Gender' ? (
                          <Field as="select" name={fieldName} className="form-control">
                            <option value="">Select Gender</option>
                            {genderOptions.map((opt) => (
                              <option key={opt} value={opt.toLowerCase()}>
                                {opt}
                              </option>
                            ))}
                          </Field>
                        ) : label === 'Timezone' ? (
                          <Field as="select" name={fieldName} className="form-control">
                            <option value="">Select Timezone</option>
                            {timezoneOptions.map((opt) => (
                              <option key={opt.label} value={opt.label}>
                                {opt.label}
                              </option>
                            ))}
                          </Field>
                        ) : label === 'Location' ? (
                          <>
                            <Select
                              options={countryOptions}
                              value={countryOptions.find(option => option.value === values[fieldName])}
                              onChange={(selectedOption) => {
                                setFieldValue(fieldName, selectedOption?.value || '');
                                setSearchQuery('');
                              }}
                              onInputChange={(inputValue) => setSearchQuery(inputValue)}
                              isSearchable={true}
                              placeholder="Search or select country..."
                              noOptionsMessage={() => "No countries found"}
                              styles={customSelectStyles}
                              formatOptionLabel={({ label }) => label}
                              className="basic-single"
                              classNamePrefix="select"
                              name={fieldName} // Add name prop
                              id={fieldName} // Add id prop
                            />
                            {/* Hidden input for Formik */}
                            <input
                              type="hidden"
                              name={fieldName}
                              value={values[fieldName] || ''}
                            />
                          </>
                        ) : label === 'Native Language' ? (
                          <Field 
                            name={fieldName}
                            component={LanguageSelect}
                            options={languages}
                            placeholder={`Select ${label}`}
                            className="form-control"
                          />
                        ) : label === 'Learning Language' ? (
                          <Field 
                            name={fieldName}
                            component={LanguageSelect}
                            options={learningLanguages}
                            placeholder={`Select ${label}`}
                            className="form-control"
                          />
                        ) : label === 'Interested In' || label === 'Purpose of Language Practice' ? (
                          <Field
                            type="text"
                            name={fieldName}
                            className="form-control"
                            placeholder={`Enter ${label.toLowerCase()}`}
                          />
                        ) : (
                          <Field
                            type={label === 'Age' ? 'number' : 'text'}
                            name={fieldName}
                            className="form-control"
                          />
                        )}
                        
                        {/* Email field helper text */}
                        {label === 'Email' && hasExistingEmail && (
                          <small className="text-muted d-block mt-1">
                            Email address cannot be changed once set.
                          </small>
                        )}
                        
                        {/* Email field warning for new users */}
                        {label === 'Email' && !hasExistingEmail && values[fieldName] && (
                          <small className="text-warning d-block mt-1">
                            Please verify your email. This cannot be changed after saving.
                          </small>
                        )}
                        
                        {/* Error message for location */}
                        {label === 'Location' && errors[fieldName] && touched[fieldName] && (
                          <div className="text-danger small mt-1">{errors[fieldName]}</div>
                        )}
                        
                        {/* Error messages for other fields */}
                        {label !== 'Location' && errors[fieldName] && touched[fieldName] && (
                          <div className="text-danger small">{errors[fieldName]}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isSubmitting || isUpdating}
                >
                  {isUpdating ? 'Updating...' : 'UPDATE'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>

      {/* Update Password */}
      <div className="card h-auto">
        <div className="card-header">
          <h6 className="title">{`Update Password ${isSocialUser ? "(Password updates are not available for users who signed in via social login.)" : ""}`}</h6>
        </div>
        <Formik
          initialValues={initialPasswordValues}
          validationSchema={PasswordSchema}
          onSubmit={handleSubmitPassword}
        >
          {({ isSubmitting, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <div className="card-body">
                <div className="col-sm-6 m-b30">
                  <label className="form-label">Old Password</label>
                  <div className="position-relative">
                    <Field
                      type={showOldPassword ? "text" : "password"}
                      name="oldPassword"
                      className="form-control"
                      disabled={isSocialUser}
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      disabled={isSocialUser}
                    >
                      {showOldPassword ? (
                        <img
                          src={hidePasswordIcon}
                          height={18}
                          alt="Hide password"
                        />
                      ) : (
                        <img
                          src={showPasswordIcon}
                          height={18}
                          alt="Show password"
                        />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="oldPassword"
                    component="small"
                    className="text-danger"
                  />
                </div>
                <div className="col-sm-6 m-b30">
                  <label className="form-label">New Password</label>
                  <div className="position-relative">
                  <Field
                    type={showPassword ? "text" : "password"}
                    name="newPassword"
                    className="form-control"
                    disabled={isSocialUser}
                  />
                  <button
                    type="button"
                    className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSocialUser}
                  >
                    {showPassword ? (
                      <img
                        src={hidePasswordIcon}
                        height={18}
                        alt="Hide password"
                      />
                    ) : (
                      <img
                        src={showPasswordIcon}
                        height={18}
                        alt="Show password"
                      />
                    )}
                  </button>
                  </div>
                  <ErrorMessage
                    name="newPassword"
                    component="small"
                    className="text-danger"
                  />
                </div>
                <div className="col-sm-6 m-b30">
                  <label className="form-label">Confirm Password</label>
                  <div className="position-relative">
                    <Field
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      className="form-control"
                      disabled={isSocialUser}
                    />
                    <button
                      type="button"
                      className="btn position-absolute top-50 end-0 translate-middle-y me-2 p-0 border-0 bg-transparent"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isSocialUser}
                    >
                      {showConfirmPassword ? (
                        <img
                          src={hidePasswordIcon}
                          height={18}
                          alt="Hide password"
                        />
                      ) : (
                        <img
                          src={showPasswordIcon}
                          height={18}
                          alt="Show password"
                        />
                      )}
                    </button>
                  </div>
                  <ErrorMessage
                    name="confirmPassword"
                    component="small"
                    className="text-danger"
                  />
                </div>
              </div>
              <div className="card-footer d-flex justify-content-end">
                <button
                  className="btn btn-warning"
                  type="submit"
                  disabled={isSubmitting || isUpdatingPassword || isSocialUser}
                >
                  {isUpdatingPassword ? 'Updating...' : 'UPDATE PASSWORD'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};